import { Check } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import type { ChoiceOption } from '../questions';

/**
 * Shared primitives for the 14 step components. Kept in one file so the
 * step files themselves stay short and declarative.
 */

// ─────────────────────────────────────────────────────────────────────────
//  ChoiceCard — large radio tile with label + hint
// ─────────────────────────────────────────────────────────────────────────

interface ChoiceCardProps {
  selected: boolean;
  onSelect: () => void;
  label: string;
  hint?: string;
  /** Optional badge in the corner — used by the colour & font previews. */
  preview?: ReactNode;
}

export function ChoiceCard({ selected, onSelect, label, hint, preview }: ChoiceCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'group relative flex flex-col gap-1.5 rounded-sm border bg-paper px-4 py-3.5 text-left transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre',
        selected
          ? 'border-ink shadow-[inset_0_0_0_1px_hsl(var(--ink))]'
          : 'border-ink/15 hover:border-ink/40',
      )}
      aria-pressed={selected}
    >
      {preview ? <div className="mb-1.5">{preview}</div> : null}
      <div className="flex items-start justify-between gap-3">
        <span
          className={cn(
            'text-sm font-medium',
            selected ? 'text-ink' : 'text-ink/90',
          )}
        >
          {label}
        </span>
        {selected ? (
          <Check className="h-4 w-4 shrink-0 text-ochre" strokeWidth={2.5} aria-hidden />
        ) : null}
      </div>
      {hint ? (
        <p className="text-xs leading-relaxed text-graphite">{hint}</p>
      ) : null}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  ChoiceGrid — convenience wrapper for radio sets
// ─────────────────────────────────────────────────────────────────────────

interface ChoiceGridProps<T extends string> {
  options: ChoiceOption<T>[];
  value: T | undefined;
  onChange: (v: T) => void;
  columns?: 1 | 2 | 3;
}

export function ChoiceGrid<T extends string>({
  options,
  value,
  onChange,
  columns = 2,
}: ChoiceGridProps<T>) {
  const colClass =
    columns === 1
      ? 'grid-cols-1'
      : columns === 2
        ? 'grid-cols-1 sm:grid-cols-2'
        : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <div role="radiogroup" className={cn('grid gap-3', colClass)}>
      {options.map((opt) => (
        <ChoiceCard
          key={opt.value}
          label={opt.label}
          hint={opt.hint}
          selected={value === opt.value}
          onSelect={() => onChange(opt.value)}
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  MultiChip — toggleable chip grid for multi-select questions
// ─────────────────────────────────────────────────────────────────────────

interface MultiChipProps<T extends string> {
  options: ChoiceOption<T>[];
  values: T[];
  onChange: (next: T[]) => void;
  /** Mutually-exclusive option (e.g. "None of the above" clears the set). */
  exclusiveValue?: T;
}

export function MultiChip<T extends string>({
  options,
  values,
  onChange,
  exclusiveValue,
}: MultiChipProps<T>) {
  const toggle = (v: T) => {
    if (exclusiveValue && v === exclusiveValue) {
      onChange(values.includes(v) ? [] : [v]);
      return;
    }
    if (values.includes(v)) {
      onChange(values.filter((x) => x !== v));
    } else {
      const next = [...values.filter((x) => x !== exclusiveValue), v];
      onChange(next);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const on = values.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={cn(
              'inline-flex items-center gap-2 rounded-sm border px-3 py-1.5 text-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre',
              on
                ? 'border-ink bg-ink text-paper'
                : 'border-ink/20 bg-paper text-ink hover:border-ink/50',
            )}
            aria-pressed={on}
          >
            {on ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : null}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
